import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ColumnSketch } from '@/shared/api/generated/catalog';

import { LOW_UNIQUENESS, dimensionReadout } from '../model/dimensions';
import { share } from '../model/presentation';

/* The six quality dimensions, as dials rather than a table of sentences.
 *
 * **A table was the wrong shape and the reason is comparability.** Six rows of name/figure/paragraph
 * read as a spreadsheet, and a spreadsheet invites a reader to scan the number column and rank them
 * — but a completeness percentage and a uniqueness percentage answer different questions and cannot
 * be ranked against each other. Six separate dials say six separate things, which is what is true.
 *
 * **Colour carries availability, never quality**, and that distinction is the whole design. A
 * measured dimension is `--status-info` whatever the figure, because 4% uniqueness is correct on a
 * `store_sk` and a duplicate key on a `customer_id` and nothing in a profile can tell those apart —
 * so green-for-high would be the screen claiming something it cannot know. What the colour does
 * separate is the three kinds of answer: measured (blue), waiting on a rule you write (amber), and
 * not obtainable from this table at all (grey). Those *are* facts about the platform.
 *
 * The long explanations live on the tile as a `title`, so a reader who wants the reasoning gets it
 * and the panel stays six tiles rather than six paragraphs.
 */

/** Circumference of the ring, for the dash-offset arithmetic. r=26 in a 64-unit box. */
const RING = 2 * Math.PI * 26;

function Ring({
  ratio,
  tone,
}: {
  readonly ratio: number;
  readonly tone: string;
}): ReactElement {
  return (
    <svg className="lq-dial__ring" viewBox="0 0 64 64" aria-hidden="true">
      <circle className="lq-dial__track" cx="32" cy="32" r="26" />
      <circle
        className="lq-dial__fill"
        cx="32"
        cy="32"
        r="26"
        stroke={tone}
        strokeDasharray={RING}
        /* The unfilled remainder as the offset, so a ratio of 0 draws nothing rather than a full
           ring — a dial that looks complete at zero is worse than no dial. */
        strokeDashoffset={RING * (1 - ratio)}
      />
    </svg>
  );
}

export function DimensionReadout({
  columns,
}: {
  readonly columns: readonly ColumnSketch[];
}): ReactElement {
  const { t } = useTranslation();
  const readings = dimensionReadout(columns);

  return (
    <div className="lq-dials">
      {readings.map((reading) => {
        const measured = reading.availability === 'measured' && reading.value !== null;
        const tone =
          reading.availability === 'measured'
            ? 'var(--status-info)'
            : reading.availability === 'needsRule'
              ? 'var(--status-warn)'
              : 'var(--status-unknown)';
        return (
          <div
            key={reading.dimension}
            className="lq-dial"
            /* The reasoning, on hover, rather than in the tile. Six paragraphs would make this a
               wall of text again, and the sentence matters most to the reader who stops on one. */
            title={t(`profile.dimension.${reading.dimension}.${reading.availability}`)}
          >
            {measured ? (
              <div className="lq-dial__gauge">
                <Ring ratio={reading.value} tone={tone} />
                <span className="lq-dial__figure lumiq-numeric">
                  {share(reading.value)}
                </span>
              </div>
            ) : (
              /* No ring for a dimension with nothing measured: an empty ring reads as zero, and
                 zero is a measurement nobody made. A dot in the same footprint keeps the six tiles
                 on one baseline without drawing a value. */
              <div className="lq-dial__gauge">
                <span className="lq-dial__dot" style={{ background: tone }} />
              </div>
            )}
            <span className="lq-dial__name">
              {t(`profile.dimension.${reading.dimension}.name`)}
            </span>
            <span className="lq-dial__note lq-muted">
              {measured
                ? reading.dimension === 'uniqueness' && reading.value < LOW_UNIQUENESS
                  ? t('profile.dimension.uniqueness.short')
                  : t('profile.dimension.columnsCounted', { count: reading.columns })
                : t(`profile.dimension.availability.${reading.availability}`)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
